// Living vines: a state ladder driven by two properties at once, rather than
// one meltsWhen rule resolving once and being done.
//
// Every other threshold-driven object in this game (barrier.meltsWhen) answers
// one question -- "has this crossed its line yet" -- and does one of three
// fixed things when it does: disappear, collapse into rubble, or toggle to a
// paired texture. Vines answer a different, ongoing question -- "which of five
// states does this currently read as" -- every time anything asks, for as long
// as the object exists. That is why this is its own small module rather than
// another barrier.meltsWhen rule: the shape genuinely differs, not just the
// numbers.
//
// The five stages, in the priority they are checked (see `vineStage`):
//
//   Entropy >= deadEntropy                    dead      (overrides everything below)
//   Energy  >= burningEnergy                   burning   (not climbable; burns Entropy in per decay tick)
//   Energy  >= growingEnergy                   growing   (climbable; attempts to grow upward)
//   Energy  >= healthyEnergy                   healthy   (climbable)
//   otherwise                                  withered  (not climbable)
//
// Nothing about *reaching* a stage is special-cased per stage. Energy rises the
// same way any barrier.meltsWhen sink's does -- an ordinary Transfer or Project,
// marked source or self-cast, ContinuousMag/Precise/Standard/Unbound all meaning
// what they always mean. Entropy falls the same way room6's Reliquary pair
// already proved out: an ordinary Transfer moving Entropy off one object and
// onto another. There is no "precise" rune here and none was added -- the
// shipped magnitude ladder already gives exactly the control the state table
// needs (Precise delivers 1, landing on Healthy; two Precises land on Growing;
// Standard delivers 4, landing on Burning) without a new modifier meaning
// something for one object that it means nowhere else.
import { ObjectDef, RoomDef } from './types';
import { PhysicsConfig, DEFAULT_PHYSICS, countedDelta } from './physics';
import { SimEvents } from './events';
import { WorldState } from './state';
import { StagePresentation, applyStagePresentation } from './presentation';
import { CLIFF_EAST, CLIFF_NORTH } from './constants';

// Not importing addObjectToRoom from mechanics.ts: that module imports
// meltRulesSatisfied from queries.ts, and queries.ts's isWalkable needs
// vineClimbable from here to decide whether a cliff tile is currently
// climbable -- importing mechanics.ts here would close that into a cycle
// (queries -> vines -> mechanics -> queries). The two lines it would have
// saved are inlined in attemptVineGrowth below instead.

export type VineStageId = 'dead' | 'withered' | 'healthy' | 'growing' | 'burning';

export interface VineStageMeta extends StagePresentation {
  /** Required here, unlike on `StagePresentation` itself: every stage of this
   * ladder renames, because a dead tangle and a climbable one are not the same
   * thing to look at and should not share a label. */
  name: string;
  climbable: boolean;
}

/** One texture family per `VinesDef.variant`. Same stage ids, same thresholds,
 * same everything else -- only what gets drawn differs, which is the whole
 * reason `variant` exists rather than a second copy of this module. `bush`/
 * `shrub`/`tree`'s own `climbable: false` at every stage is belt-and-braces:
 * `vineClimbable` already short-circuits on `VinesDef.climbs === false` before
 * ever consulting this table for those three, but a stale `true` sitting
 * here unused would be a landmine for whoever next reads this table
 * expecting it to be authoritative. */
export const VINE_TEXTURES: Record<'plain' | 'stone' | 'bush' | 'shrub' | 'tree', Record<VineStageId, VineStageMeta>> = {
  plain: {
    dead: {
      name: 'Dead Vines',
      texture: 'object-vines-dead',
      climbable: false,
      description: 'A tangle of blackened, brittle vines. Whatever grew here burned too hot, too long. Draining the Entropy that killed it -- an ordinary Transfer -- is the only way anything grows back.',
    },
    withered: {
      name: 'Withered Vines',
      texture: 'object-vines-withered',
      climbable: false,
      description: 'Pale, dry vines, clinging to the rock without much left in them. Not dead -- just not enough Energy in them to hold real weight yet.',
    },
    healthy: {
      name: 'Vines',
      texture: 'object-vines-healthy',
      climbable: true,
      description: 'A healthy tangle of vines, thick enough to climb.',
    },
    growing: {
      name: 'Growing Vines',
      texture: 'object-vines-growing',
      climbable: true,
      description: "Vines reaching upward, visibly still growing. Given a moment, they'll take root in whatever's above them.",
    },
    burning: {
      name: 'Burning Vines',
      texture: 'object-vines-burning',
      climbable: false,
      description: "Vines, on fire. Whatever's left once this burns out won't be climbable.",
    },
  },
  stone: {
    dead: {
      name: 'Stone Block with Dead Vines',
      texture: 'object-vines-stone-dead',
      climbable: false,
      description: 'A stone block, wrapped in blackened, brittle vines. Whatever grew here burned too hot, too long.',
    },
    withered: {
      name: 'Stone Block with Withered Vines',
      texture: 'object-vines-stone-withered',
      climbable: false,
      description: 'A stone block with pale, dry vines clinging to it -- not enough Energy in them to hold real weight yet.',
    },
    healthy: {
      name: 'Stone Block with Vines',
      texture: 'object-vines-stone-healthy',
      climbable: true,
      description: 'A stone block, wrapped in a healthy tangle of vines, thick enough to climb. Still just a block -- it pushes like any other.',
    },
    growing: {
      name: 'Stone Block with Growing Vines',
      texture: 'object-vines-stone-growing',
      climbable: true,
      description: 'A stone block with vines visibly still growing over it.',
    },
    burning: {
      name: 'Stone Block with Burning Vines',
      texture: 'object-vines-stone-burning',
      climbable: false,
      description: "A stone block, its vines on fire. The stone itself won't burn, but the vines will.",
    },
  },
  bush: {
    dead: {
      name: 'Dead Bush',
      texture: 'object-bush-dead',
      climbable: false,
      description: 'A blackened, brittle bush, burned through to bare stems. Draining the Entropy that killed it -- an ordinary Transfer -- is the only way it comes back.',
    },
    withered: {
      name: 'Withered Bush',
      texture: 'object-bush-withered',
      climbable: false,
      description: 'A pale, thinning bush, more twig than leaf. Not dead -- just short on Energy.',
    },
    healthy: {
      name: 'Bush',
      texture: 'object-bush-healthy',
      climbable: false,
      description: 'A healthy, leafy bush.',
    },
    growing: {
      name: 'Flourishing Bush',
      texture: 'object-bush-growing',
      climbable: false,
      description: 'A bush in full leaf, fuller than healthy.',
    },
    burning: {
      name: 'Burning Bush',
      texture: 'object-bush-burning',
      climbable: false,
      description: 'A bush, on fire.',
    },
  },
  shrub: {
    dead: {
      name: 'Dead Shrub',
      texture: 'object-shrub-dead',
      climbable: false,
      description: 'A low, blackened shrub, burned down to bare twigs. Draining the Entropy that killed it -- an ordinary Transfer -- is the only way it comes back.',
    },
    withered: {
      name: 'Withered Shrub',
      texture: 'object-shrub-withered',
      climbable: false,
      description: 'A low shrub, pale and sparse. Not dead -- just short on Energy.',
    },
    healthy: {
      name: 'Shrub',
      texture: 'object-shrub-healthy',
      climbable: false,
      description: 'A low, healthy shrub.',
    },
    growing: {
      name: 'Flourishing Shrub',
      texture: 'object-shrub-growing',
      climbable: false,
      description: 'A shrub in full leaf, fuller than healthy.',
    },
    burning: {
      name: 'Burning Shrub',
      texture: 'object-shrub-burning',
      climbable: false,
      description: 'A shrub, on fire.',
    },
  },
  tree: {
    dead: {
      name: 'Dead Tree',
      texture: 'object-tree-dead',
      climbable: false,
      description: 'A dead tree, its bare branches scorched black. Draining the Entropy that killed it -- an ordinary Transfer -- is the only way it comes back.',
    },
    withered: {
      name: 'Withered Tree',
      texture: 'object-tree-withered',
      climbable: false,
      description: 'A thin, pale tree, its canopy sparse. Not dead -- just short on Energy.',
    },
    healthy: {
      name: 'Tree',
      texture: 'object-tree-healthy',
      climbable: false,
      description: 'A healthy tree, its canopy full.',
    },
    growing: {
      name: 'Flourishing Tree',
      texture: 'object-tree-growing',
      climbable: false,
      description: 'A tree in full leaf, its canopy fuller than healthy.',
    },
    burning: {
      name: 'Burning Tree',
      texture: 'object-tree-burning',
      climbable: false,
      description: 'A tree, on fire.',
    },
  },
};

function textureFamily(obj: ObjectDef): 'plain' | 'stone' | 'bush' | 'shrub' | 'tree' {
  return obj.vines?.variant ?? 'plain';
}

/** Which of the five stages an object's current Entropy/Energy puts it in.
 * Pure, and cheap enough to call on every settle and every decay tick rather
 * than caching it anywhere -- the object's own `properties` are the only
 * state that matters, the same "recomputed live, never stored" shape
 * `barrier.reformable` already uses. */
export function vineStage(obj: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS): VineStageId {
  const entropy = obj.properties?.entropy ?? 0;
  const energy = obj.properties?.energy ?? 0;
  const v = physics.vines;
  if (entropy >= v.deadEntropy) return 'dead';
  if (energy >= v.burningEnergy) return 'burning';
  if (energy >= v.growingEnergy) return 'growing';
  if (energy >= v.healthyEnergy) return 'healthy';
  return 'withered';
}

/** Whether an object with a `vines` cluster currently grants passage on
 * climbable terrain (see `isWalkable`). Not climbable while withered (not
 * enough growth to bear weight), burning (it's on fire) or dead -- or, for a
 * Bush/Shrub/Tree, ever: `VinesDef.climbs === false` overrides the stage
 * table outright, since those are dressing on ordinary floor, not a rope on
 * a cliff face. */
export function vineClimbable(obj: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS): boolean {
  if (obj.vines?.climbs === false) return false;
  return VINE_TEXTURES[textureFamily(obj)][vineStage(obj, physics)].climbable;
}

/** Re-derives this object's whole presentation -- name, texture and Inspect
 * description -- from the stage it currently reads (see `presentation.ts`,
 * which is the one place all three move together).
 *
 * A real, reported bug before any of this existed: a vines object's Inspect
 * text never changed across withered/healthy/growing/burning/dead at all,
 * since nothing ever touched `interact.text` after the object was authored (or,
 * for a grown segment, after `attemptVineGrowth`'s one-time Healthy
 * description). The description was fixed then and the *name* was not, which is
 * the second half of the same report (2026-08-11): a blackened, brittle, dead
 * tangle was still labelled "Vines".
 *
 * Called after every settle (a cast touched some object in the room, possibly
 * this one) and after every decay tick (Energy/Entropy just moved on their
 * own) -- the same two call shapes `checkMeltThreshold` already uses for a
 * reformable barrier's own live texture. Safe to call unconditionally on
 * every vines object every time; the no-op case (nothing changed) costs three
 * string comparisons. */
export function refreshVineTexture(obj: ObjectDef, physics: PhysicsConfig, out: SimEvents): void {
  applyStagePresentation(obj, VINE_TEXTURES[textureFamily(obj)][vineStage(obj, physics)], out);
}

/** Ambient effects for one decay tick: Energy drifts toward the room's
 * ambient the same way any barrier.meltsWhen sink's does, and -- checked
 * against the stage *before* this tick's own decay, so a cast that lands
 * exactly on the burning threshold still counts as having burned for the
 * tick that crossed it -- sustained heat irreversibly adds to Entropy. A
 * single Standard-magnitude overshoot (delivers exactly the burning
 * threshold) singes and cools back out within one tick; an Unbound cast (or
 * a second cast before it cools) stays in the burning band long enough to
 * cross into permanently dead. Mirrors `applyRoomDecay`'s own per-object
 * shape (decay first, then react to the result) rather than adding a branch
 * inside that function -- vines never use `barrier.meltsWhen`, so they were
 * never going to be reached by its existing guard. */
export function decayVines(obj: ObjectDef, ambientEnergy: number, physics: PhysicsConfig, out: SimEvents): void {
  if (!obj.properties) return;
  const wasBurning = vineStage(obj, physics) === 'burning';
  if (wasBurning) obj.properties.entropy = (obj.properties.entropy ?? 0) + physics.vines.burnEntropyPerTick;
  const energy = obj.properties.energy ?? 0;
  obj.properties.energy = energy + countedDelta(ambientEnergy - energy, physics.room.decayPerTick);
  refreshVineTexture(obj, physics, out);
}

/** True for a cliff tile a climbing object could occupy -- both orientations
 * behave identically (see `constants.ts`'s CLIFF_NORTH/CLIFF_EAST doc
 * comment); the direction is a rendering choice, not a mechanical one. */
function isClimbableTerrain(room: RoomDef, x: number, y: number): boolean {
  const tileId = room.data[y]?.[x];
  return tileId === CLIFF_NORTH || tileId === CLIFF_EAST;
}

/** If `obj` is in the Growing stage and its own `vines.grows` has not been
 * set to false, attempts to plant a new, Healthy vines segment directly
 * above it -- only if that tile is climbable terrain and nothing already
 * occupies it. A silent no-op otherwise: the user only asked for growth "if
 * it is a cliff tile", and an empty target is exactly as valid a reason to
 * do nothing as a non-cliff one.
 *
 * Deliberately not one-shot / not flagged as "already grew from here". The
 * physical result *is* the memory: once the tile above is occupied, the same
 * check fails on every later call, the same way a reformable barrier needs
 * no separate "already open" flag beyond its own current properties. A vine
 * that decays back out of Growing and is pushed into it again by a second
 * cast will attempt again -- and either find the tile still occupied by its
 * own earlier growth (no-op) or, if that growth was somehow removed, grow a
 * fresh one. New growth starts Healthy, not Growing -- stable and immediately
 * climbable rather than itself trying to grow further on the same cast,
 * which is what keeps one enthusiastic cast from chaining all the way up an
 * arbitrarily tall cliff in a single settle.
 *
 * **A pushable plant is a seed you can carry** (2026-08-11). The moveable
 * Stone Block with Vines used to be excluded from this by an authored
 * `grows: false`, on the worry that a pushable object could plant a permanent
 * growth into "whatever tile happens to be above it at the moment" and then be
 * carried off, orphaning it. That worry does not survive the check two lines
 * down: growth only ever lands on *climbable terrain*, which is tilemap, which
 * nothing can move or destroy. A segment planted from a block is rooted in the
 * cliff it grew onto, exactly like one grown from the vines already hanging
 * there, and pushing the block away afterward leaves it no more orphaned than
 * anything else growing on rock. Shoving a vine-covered block up against a
 * cliff face and feeding it Energy until it takes is a route the player
 * *builds*, which is the interesting half of having a plant that moves --
 * a reported bug precisely because the world showed it a block covered in
 * living, growing vines pressed against bare rock, and then nothing
 * happened. */
export function attemptVineGrowth(world: WorldState, room: RoomDef, obj: ObjectDef, out: SimEvents): void {
  if (obj.vines?.grows === false) return;
  if (vineStage(obj, world.physics) !== 'growing') return;
  const targetX = obj.x;
  const targetY = obj.y - 1;
  if (!isClimbableTerrain(room, targetX, targetY)) return;
  if (room.objects.some((o) => o.x === targetX && o.y === targetY)) return;

  // Always the plain family, whatever grew it: what takes root on the cliff is
  // vines on rock, not a second stone block.
  const healthy = VINE_TEXTURES.plain.healthy;
  const grown: ObjectDef = {
    x: targetX,
    y: targetY,
    id: `vine-${world.nextVineId++}`,
    name: healthy.name,
    texture: healthy.texture,
    // Load-bearing, not decoration: isWalkable's cliff-tile branch only
    // decides whether the *tile* is currently climbable. Its second,
    // ordinary check still treats any object sitting on that tile as solid
    // unless the object itself says otherwise -- exactly like a Riverbed
    // tile or a Signpost. Every authored Vines object meant to be climbed
    // needs this set for the same reason; see VinesDef's own doc comment.
    walkable: true,
    vines: {},
    properties: { entropy: 0, energy: world.physics.vines.healthyEnergy },
    interact: { label: 'Inspect', text: healthy.description },
  };
  room.objects.push(grown);
  out.push({ kind: 'object-added', object: grown });
}

/** The declared system's own body (see `settle.ts`): re-derives every vines
 * object's texture for whatever just changed, then lets any that reached
 * Growing attempt to spread upward. Texture refresh runs first and
 * unconditionally -- growth only cares about objects already in the Growing
 * stage, and a cast that just raised one there needs its own texture caught
 * up before (or regardless of) whether it manages to spread. */
export function settleVines(world: WorldState, out: SimEvents): void {
  const room = world.rooms[world.player.roomId];
  if (!room) return;
  for (const obj of room.objects) {
    if (!obj.vines) continue;
    refreshVineTexture(obj, world.physics, out);
  }
  // Snapshot before iterating: attemptVineGrowth can push a new object into
  // room.objects, and a for..of over the live array would otherwise walk
  // straight into growing the thing this pass just grew.
  for (const obj of [...room.objects]) {
    if (obj.vines) attemptVineGrowth(world, room, obj, out);
  }
}
